import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

const TABS = [
  { id: "received", label: "Received" },
  { id: "sent", label: "Sent" },
];

const STATUS_STYLE = {
  pending: { text: "Pending", cls: "sb-badge-soft" },
  accepted: { text: "Accepted", cls: "sb-badge-soft sb-badge-gold" },
  declined: { text: "Declined", cls: "sb-badge-soft", extra: "text-danger" },
};

export default function Interests() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("received");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") {
      navigate("/login", { replace: true, state: { from: "/interests" } });
    }
  }, [user, loading, navigate]);

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get(`/interests/${tab}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (user && typeof user === "object") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  const respond = async (id, status) => {
    setBusy(id);
    try {
      await api.post(`/interests/${id}/respond`, { status });
      toast.success(status === "accepted" ? "Interest accepted 🌸" : "Interest declined");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  if (loading || !user || typeof user !== "object") {
    return (
      <div>
        <Navbar />
        <div className="container py-5"><div className="sb-card">Loading…</div></div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container py-4" data-testid="interests-page">
        <div className="d-flex justify-content-between align-items-end flex-wrap gap-2 mb-4">
          <div>
            <div className="sb-kicker mb-1">Express Interest</div>
            <h1 className="h2 mb-1">Interests.</h1>
            <p className="text-muted mb-0">Accept to unlock mutual contact.</p>
          </div>
          <div className="btn-group" role="group">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={"btn btn-sm " + (tab === t.id ? "btn-primary" : "btn-outline-primary")}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {items === null ? (
          <div className="sb-card">Loading…</div>
        ) : items.length === 0 ? (
          <div className="sb-card text-center py-5" data-testid="empty-interests">
            <div className="sb-kicker mb-2">Nothing here yet</div>
            <h3 className="h5">
              {tab === "received"
                ? "You haven't received any interests yet."
                : "You haven't sent any interests yet."}
            </h3>
            {tab === "sent" && (
              <Link to="/search" className="btn btn-primary mt-2">Find matches</Link>
            )}
          </div>
        ) : (
          <div className="row g-3">
            {items.map((it) => {
              const s = STATUS_STYLE[it.status] || STATUS_STYLE.pending;
              const showSender = tab === "received";
              const displayName = showSender ? it.from_name : it.to_name;
              const photo = showSender ? it.from_photo_url : it.to_photo_url;
              const profileId = showSender ? it.from_profile_id : it.to_profile_id;
              return (
                <div className="col-md-6 col-xl-4" key={it.id}>
                  <div className="sb-card h-100" data-testid={`interest-${it.id}`}>
                    <div className="d-flex gap-3 align-items-center mb-3">
                      <div className="sb-interest-photo">
                        {photo ? (
                          <img src={photo} alt={displayName} />
                        ) : (
                          <span>{(displayName || "?").charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex-fill">
                        <div className="fw-semibold" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem" }}>
                          {displayName || "Member"}
                        </div>
                        <div className="small text-muted">
                          {new Date(it.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <span className={`${s.cls} ${s.extra || ""}`}>{s.text}</span>
                    </div>

                    {it.message && (
                      <p className="mb-3 small" style={{ color: "var(--sb-body)" }}>
                        “{it.message}”
                      </p>
                    )}

                    <div className="d-flex gap-2 mt-auto">
                      {profileId && (
                        <Link
                          to={`/profile/${profileId}`}
                          className="btn btn-outline-primary btn-sm"
                          data-testid={`view-${it.id}`}
                        >
                          View profile
                        </Link>
                      )}
                      {tab === "received" && it.status === "pending" && (
                        <>
                          <button
                            className="btn btn-gold btn-sm"
                            disabled={busy === it.id}
                            onClick={() => respond(it.id, "accepted")}
                            data-testid={`accept-${it.id}`}
                          >
                            Accept
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm rounded-pill"
                            disabled={busy === it.id}
                            onClick={() => respond(it.id, "declined")}
                            data-testid={`decline-${it.id}`}
                          >
                            Decline
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
